import { Component, Input, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TaxPayerService } from 'src/app/services/taxPayer.service';

@Component({
  selector: 'app-company-profile-photo',
  templateUrl: './company-profile-photo.component.html',
  styleUrls: ['./company-profile-photo.component.css']
})
export class CompanyProfilePhotoComponent implements OnChanges, OnDestroy {
@ViewChild('videoEl', { static: false }) videoEl: ElementRef<HTMLVideoElement>;
@ViewChild('canvasEl', { static: false }) canvasEl: ElementRef<HTMLCanvasElement>;
  @Input() tin;
  @Input() canUpdate = false;
  @Input() photoPath;
  photoExists
  photoUrl = null;
  private objectUrl = null;
  private previewBlob: Blob = null;
  private stream: MediaStream = null;
  cameraOpen = false;
  previewOpen = false;
  previewUrl = null;
  private previewObjectUrl = null;
  taxPayerPhoto = null

  constructor(
    private taxPayerService: TaxPayerService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.revokeObjectUrl();
    this.photoUrl = 'assets/img/profile.png';
    if (!this.tin) return;
    if (!this.photoPath) return;

    this.loadPhoto();
  }
  async toggleCamera() {
    if (this.cameraOpen) {
      this.stopCamera();
      return;
    }
    await this.startCamera();
  }
  
  private async startCamera() {
    this.discardPreview();
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      this.cameraOpen = true;
      this.cdr.detectChanges();
      setTimeout(async () => {
        if (!this.videoEl || !this.videoEl.nativeElement) return;
        const v = this.videoEl.nativeElement;
        v.srcObject = this.stream;
        await v.play().catch(() => {});
        await this.waitForVideoReady(v);
      }, 0);
    } catch (err) {
      console.error('Camera access failed', err);
  
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        this.cameraOpen = true;
        setTimeout(() => {
          if (!this.videoEl || !this.videoEl.nativeElement) return;
          this.videoEl.nativeElement.srcObject = this.stream;
          this.videoEl.nativeElement.play().catch(() => {});
        }, 0);
      } catch (err2) {
        console.error('Camera fallback failed', err2);
        this.cameraOpen = false;
        this.stream = null;
      }
    }
  }
  
  stopCamera() {
    this.cameraOpen = false;
    if (this.videoEl && this.videoEl.nativeElement) {
      this.videoEl.nativeElement.pause();
      this.videoEl.nativeElement.srcObject = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
  }
  
  async captureToPreview() {
    if (!this.videoEl || !this.canvasEl) return;
    const video = this.videoEl.nativeElement;
    const canvas = this.canvasEl.nativeElement;
    await this.waitForVideoReady(video);
    if (!video.videoWidth || !video.videoHeight) return;
    const size = 200;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const minSide = Math.min(videoWidth, videoHeight);
    const sx = (videoWidth - minSide) / 2;
    const sy = (videoHeight - minSide) / 2;

    ctx.drawImage( video,sx, sy,minSide, minSide,0, 0,size, size );
  
    canvas.toBlob((blob) => {
      if (!blob) return;
      this.previewBlob = blob;
      if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = URL.createObjectURL(blob);
      this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(this.previewObjectUrl);
      this.stopCamera();
      this.previewOpen = true;
      this.cdr.detectChanges();
    }, 'image/jpeg', 0.92);
  }
  
  async savePreview() {
    if (!this.previewBlob) return;
    const file = new File([this.previewBlob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
    try {
      await this.taxPayerService.uploadTaxpayerPhoto(this.tin, file).toPromise();
      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(this.previewBlob);
      this.photoUrl = this.sanitizer.bypassSecurityTrustUrl(this.objectUrl);
      this.discardPreview();
      setTimeout(() => this.loadPhoto(), 300);
    } catch (err) {
      console.error('Upload captured photo failed', err);
    }
  }
  
  discardPreview() {
    this.previewOpen = false;
    this.previewBlob = null;
    this.previewUrl = null;
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
    this.cdr.detectChanges();
  }
  
  async retake() {
    this.discardPreview();
    await this.startCamera();
  }
  
  ngOnDestroy() {
    this.revokeObjectUrl();
    this.stopCamera();
    this.discardPreview();
  }  

  private revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  async loadPhoto() {
    if (!this.tin) return;
    this.taxPayerService.getTaxpayerPhotoBlob(this.tin).subscribe((blob) => {
      this.revokeObjectUrl();
      if (!blob || blob.size === 0) {
        this.photoUrl = 'assets/img/profile.png';
        return;
      }
      this.objectUrl = URL.createObjectURL(blob);
      this.photoUrl = this.sanitizer.bypassSecurityTrustUrl(this.objectUrl);
    });
  }

  async onPhotoSelected(event) {
    let file = event.target.files && event.target.files[0];
    if (!file) return;
    try {
      if (!file.type || !file.type.startsWith('image/')) {
        console.error('Only image files allowed');
        return;
      }
      file = await this.compressImageToMax1MB(file);
      await this.taxPayerService.uploadTaxpayerPhoto(this.tin, file).toPromise();
      this.loadPhoto();
    } catch (err) {
      console.error('Upload taxpayer photo failed', err);
    } finally {
      event.target.value = '';
    }
  }

  async removePhoto() {
    try {
      await this.taxPayerService.deleteTaxpayerPhoto(this.tin).toPromise();
      this.revokeObjectUrl();
      this.photoUrl = 'assets/img/profile.png';
    } catch (err) {
      console.error('Remove taxpayer photo failed', err);
    }
  }

  private waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve) => {
      if (video.videoWidth > 0 && video.videoHeight > 0) return resolve();
      const done = () => resolve();
      video.addEventListener('loadedmetadata', done, { once: true });
      video.addEventListener('canplay', done, { once: true });
    });
  }

  private async compressImageToMax1MB(file: File): Promise<File> {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      throw new Error('NOT_IMAGE');
    }
  
    const MAX_BYTES = 1 * 1024 * 1024;
    if (file.size <= MAX_BYTES) return file;
  
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('READ_FAIL'));
      reader.readAsDataURL(file);
    });
  
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('IMG_LOAD_FAIL'));
      i.src = dataUrl;
    });
  
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('NO_CANVAS');
  
    const MAX_DIM = 1600;
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
  
    if (w > MAX_DIM || h > MAX_DIM) {
      const scale = Math.min(MAX_DIM / w, MAX_DIM / h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
  
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(img, 0, 0, w, h);
  
    let quality = 0.92;
    let outBlob: Blob = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', quality));
  
    while (outBlob && outBlob.size > MAX_BYTES && quality > 0.35) {
      quality = Math.round((quality - 0.07) * 100) / 100;
      outBlob = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', quality));
    }
  
    if (!outBlob) throw new Error('TOBLOB_FAIL');
  
    while (outBlob.size > MAX_BYTES && w > 600 && h > 600) {
      w = Math.round(w * 0.85);
      h = Math.round(h * 0.85);
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(img, 0, 0, w, h);
  
      quality = Math.min(quality, 0.75);
      outBlob = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', quality));
      if (!outBlob) break;
    }
  
    const newName = (file.name || 'photo')
      .replace(/\.(png|jpg|jpeg|webp)$/i, '') + '.jpg';
  
    return new File([outBlob], newName, { type: 'image/jpeg' });
  }  
}
